import { useMemo } from 'react';
import { REDUX_STORE_SLICES } from '../../services/exports/Constants';
import useStore from '../store/useStore';
import useOffersStuff from '../../features/offers/hooks/useOffersStuff';
import useGetPromoCodesApi from '../api/companies/useGetPromoCodesApi';
import useWelcomeBonus from '../../components-library/carousel/useWelcomeBonus';
import useCashbackProgram from '../cashback/useCashbackProgram';
import useAuth from '../global/useAuth';
import useMobileApp from '../mobile-app/useMobileApp';
import { CompanyDetailsInterface, Franchise, Offer, SmartPricingRule } from '../../services/exports/Interfaces';
import { PromoCodeData } from '../../store/api-slices';

interface UseDealsReturn {
  activeOffers: Offer[];
  promoCodes: PromoCodeData[];
  smartPricingDeals: SmartPricingRule[];
  showMasterPromoCode: boolean;
  hasImages: boolean;
  franchise: Franchise | null;
  company: CompanyDetailsInterface | null;
}

export default function useDeals(): UseDealsReturn {
  const { company, franchise } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { data: promoCodes } = useGetPromoCodesApi();
  const { activeOffers } = useOffersStuff();
  const { cashbackProgram, account } = useCashbackProgram();
  const { isLoggedIn } = useAuth();
  const { isMobileApp } = useMobileApp();
  // useWelcomeBonus returns undefined when the card isn't shown (mobile app / logged in / not configured),
  // so `.image` only counts a generated welcome-bonus image when that card actually renders.
  const welcomeBonus = useWelcomeBonus();

  const smartPricingDeals: SmartPricingRule[] = company?.smart_pricing_deals ?? [];
  const _promoCodes: PromoCodeData[] = promoCodes ?? [];

  // Mirrors the visibility gate in useMobileAppSignupBonus (which we can't import here — it pulls in the
  // mobile module). Only counts a generated signup-bonus image when that card is actually shown.
  const showsMobileAppSignupBonus =
    !!cashbackProgram?.mobile_app_signup_bonus &&
    !!franchise?.mobile_app?.app_bundle_identifier &&
    !!franchise?.mobile_app?.uuid &&
    !!franchise?.mobile_app?.asc_app_id &&
    !!franchise?.mobile_app?.released &&
    !(isLoggedIn && isMobileApp) &&
    !(isLoggedIn && account?.has_mobile_app_signup_bonus);

  const hasImages = useMemo(
    () =>
      activeOffers.some((o) => !!o.image_url) ||
      _promoCodes.some((p) => !!p.image_url) ||
      smartPricingDeals.some((s) => !!s.image_url) ||
      !!welcomeBonus?.image ||
      (showsMobileAppSignupBonus && !!cashbackProgram?.app_signup_bonus_image_url),
    [activeOffers, _promoCodes, smartPricingDeals, welcomeBonus?.image, showsMobileAppSignupBonus, cashbackProgram],
  );

  return {
    activeOffers,
    promoCodes: _promoCodes,
    smartPricingDeals,
    showMasterPromoCode: !!company?.show_master_promo_code,
    hasImages,
    franchise: franchise ?? null,
    company: company ?? null,
  };
}
